import { httpClient } from "../../httpClient";

const apiUrl = "/api";

function buildItemFormData(data: any, clubId: number | null): FormData {
  const fd = new FormData();
  const effectiveClubId = data.club_id ?? clubId;
  if (effectiveClubId) fd.append("club_id", String(effectiveClubId));
  fd.append("name", String(data.name ?? ""));
  if (data.description) fd.append("description", String(data.description));
  fd.append("price", String(data.price ?? 0));
  fd.append("has_imprint", data.has_imprint ? "true" : "false");
  fd.append("active", data.active !== false ? "true" : "false");
  const sizesRaw: string = data.sizes_text ?? "";
  const sizes = sizesRaw
    .split(",")
    .map((s: string) => s.trim())
    .filter(Boolean);
  if (sizes.length > 0) fd.append("sizes", JSON.stringify(sizes));
  const newImagesRaw = data.images_new;
  const newImagesList = Array.isArray(newImagesRaw)
    ? newImagesRaw
    : newImagesRaw
    ? [newImagesRaw]
    : [];
  for (const img of newImagesList) {
    if (img?.rawFile instanceof File) fd.append("images[]", img.rawFile);
  }
  return fd;
}

export const shopItemApi = {
  reorder: (clubId: number, order: { id: number; sort_order: number }[]) =>
    httpClient(`${apiUrl}/shop/items/reorder?club_id=${clubId}`, {
      method: "PATCH",
      body: JSON.stringify(order),
    }),
};

export const shopImageApi = {
  delete: (itemId: number, imageId: number) =>
    httpClient(`${apiUrl}/shop/items/${itemId}/images/${imageId}`, { method: "DELETE" }),

  reorder: (itemId: number, order: { id: number; sort_order: number }[]) =>
    httpClient(`${apiUrl}/shop/items/${itemId}/images/reorder`, {
      method: "PATCH",
      body: JSON.stringify(order),
    }),

  add: (itemId: number, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("images[]", f));
    return httpClient(`${apiUrl}/shop/items/${itemId}/images`, { method: "POST", body: fd });
  },
};

export function shopResource(clubId: number | null) {
  return {
    items: {
      getList: async (params: any) => {
        const qs = new URLSearchParams();
        if (clubId) qs.set("club_id", String(clubId));
        if (params?.filter?.active != null) qs.set("active", String(params.filter.active));
        const page = params?.pagination?.page ?? 1;
        const perPage = params?.pagination?.perPage ?? 100;
        qs.set("page", String(page));
        qs.set("perPage", String(perPage));
        const { json } = await httpClient(`${apiUrl}/shop/items?${qs.toString()}`);
        const data = Array.isArray(json) ? json : json?.data ?? [];
        return { data, total: json?.total ?? data.length };
      },

      getOne: async (params: any) => {
        const { json } = await httpClient(`${apiUrl}/shop/items/${params.id}`);
        return {
          data: {
            ...json,
            images: json.images ?? [],
            sizes_text: Array.isArray(json.sizes) ? json.sizes.join(", ") : "",
          },
        };
      },

      create: async (params: any) => {
        const fd = buildItemFormData(params.data, clubId);
        const { json } = await httpClient(`${apiUrl}/shop/items`, { method: "POST", body: fd });
        return { data: json };
      },

      update: async (params: any) => {
        const fd = buildItemFormData(params.data, clubId);
        const { json } = await httpClient(`${apiUrl}/shop/items/${params.id}`, {
          method: "PUT",
          body: fd,
        });
        return { data: json };
      },

      delete: async (params: any) => {
        await httpClient(`${apiUrl}/shop/items/${params.id}`, { method: "DELETE" });
        return { data: { id: params.id } };
      },
    },

    orders: {
      getList: async (params: any) => {
        const qs = new URLSearchParams();
        if (clubId) qs.set("club_id", String(clubId));
        const page = params?.pagination?.page ?? 1;
        const perPage = params?.pagination?.perPage ?? 25;
        qs.set("page", String(page));
        qs.set("perPage", String(perPage));
        const { json } = await httpClient(`${apiUrl}/shop/orders?${qs.toString()}`);
        const data = Array.isArray(json) ? json : json?.data ?? [];
        return { data, total: json?.total ?? data.length };
      },

      getOne: async (params: any) => {
        const { json } = await httpClient(`${apiUrl}/shop/orders/${params.id}`);
        return { data: json };
      },

      create: async (params: any) => {
        const payload = { ...params.data, club_id: params.data.club_id ?? clubId };
        const { json } = await httpClient(`${apiUrl}/shop/orders`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        return { data: json };
      },

      update: async (params: any) => {
        const { json } = await httpClient(`${apiUrl}/shop/orders/${params.id}`, {
          method: "PATCH",
          body: JSON.stringify(params.data),
        });
        return { data: json ?? { ...params.previousData, ...params.data } };
      },

      delete: async (params: any) => {
        await httpClient(`${apiUrl}/shop/orders/${params.id}`, { method: "DELETE" });
        return { data: { id: params.id } };
      },
    },
  };
}
